/**
 * Affinite Product Filter - Frontend JavaScript (Refactored)
 * Systém pro filtrování produktů s cacheováním a optimalizacemi
 * 
 * Refactored to use modular architecture:
 * - Core modules: FilterManager, FilterConfigBuilder, FilterState
 * - API modules: FilterApiClient
 * - Cache modules: CacheManager, FilteringCache
 * - Renderer modules: ProductRenderer, PaginationRenderer, StatsRenderer
 * - UI modules: FilterUI, ChipsManager, LoadingManager, UrlManager
 */

/**
 * Format price according to WooCommerce settings
 * 
 * @param {number} price - Price value to format
 * @param {boolean} showDecimals - Whether to show decimal places (default: false for sliders)
 * @returns {string} Formatted price string
 */
function formatWooCommercePrice(price, showDecimals = false) {
    const texts = window.affiniteProductFilterTexts || {};
    const symbol = texts.currencySymbol || '$';
    const position = texts.currencyPosition || 'left';
    const decimals = showDecimals ? (texts.priceDecimals || 2) : 0;
    const thousandSep = texts.priceThousandSep || ',';
    const decimalSep = texts.priceDecimalSep || '.';
    
    // Format number with thousand and decimal separators
    const formattedNumber = formatNumber(price, decimals, decimalSep, thousandSep);
    
    // Apply currency position
    switch (position) {
        case 'left':
            return symbol + formattedNumber;
        case 'right':
            return formattedNumber + symbol;
        case 'left_space':
            return symbol + '\u00A0' + formattedNumber; // Non-breaking space
        case 'right_space':
            return formattedNumber + '\u00A0' + symbol; // Non-breaking space
        default:
            return symbol + formattedNumber;
    }
}

/**
 * Format number with thousand and decimal separators
 * 
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places
 * @param {string} decimalSep - Decimal separator
 * @param {string} thousandSep - Thousand separator
 * @returns {string} Formatted number string
 */
function formatNumber(num, decimals, decimalSep, thousandSep) {
    const numStr = num.toFixed(decimals);
    const parts = numStr.split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Add thousand separators
    if (thousandSep) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
    }
    
    // Combine with decimal part if needed
    if (decimals > 0 && decimalPart) {
        return integerPart + decimalSep + decimalPart;
    }
    
    return integerPart;
}

class AffiniteProductFilter {
    constructor(options = {}) {
        this.options = {
            apiUrl: window.affiniteProductFilterTexts?.restUrl + 'filter' || '/wp-json/affinite-product-filter/v1/filter',
            configUrl: window.affiniteProductFilterTexts?.configUrl || '/wp-json/affinite-product-filter/v1/filter/config',
            ajaxUrl: window.affiniteProductFilterTexts?.ajaxUrl || '/wp-admin/admin-ajax.php',
            perPage: window.affiniteProductFilterTexts?.perPage || 15,
            cacheEnabled: false, // TEMPORARILY DISABLED FOR TESTING - see WORK_LOG.md
            cacheTime: 5 * 60 * 1000, // 5 minutes
            debounceTime: 300,
            chunkSize: 50,
            placeholderTemplate: '<div class="product-placeholder">Načítání...</div>',
            ...options
        };

        // Initialize modules
        this.initializeModules();

        // Legacy properties for backward compatibility
        this.currentFilters = {};
        this.currentPage = 1;
        this.currentSort = null;
        this.isLoading = false;
        this.isLoadingFromUrl = false; // Flag to prevent filter application when loading from URL
        this.pendingRequests = new Map();
        this.debounceTimers = new Map();
        this.paginationInstance = null;
        this.lastResults = null; // Store last results for infinite scroll
        this.availableOptionsAbortController = null; // AbortController pro updateFilterOptions()

        // Debug logging
        this.debugLog = [];
        this.maxLogEntries = 50;

        // Initialize (async)
        this.init().catch(error => {
            console.error('Chyba při inicializaci:', error);
        });
    }

    /**
     * Initialize all modules
     * 
     * @returns {void}
     */
    initializeModules() {
        // Core modules
        this.filterState = new FilterState();
        this.filterManager = new FilterManager();
        this.configBuilder = new FilterConfigBuilder();

        // API module
        this.apiClient = new FilterApiClient(this.options.apiUrl);

        // Cache modules
        const filteringCache = new FilteringCache({
            enabled: this.options.cacheEnabled,
            ttl: this.options.cacheTime,
            maxMemoryEntries: 100,
            storageName: 'affinite-filter-cache'
        });
        this.cacheManager = new CacheManager(filteringCache, this.options.cacheEnabled);
        // Legacy cache reference for backward compatibility
        this.cache = filteringCache;

        // Renderer modules
        this.productRenderer = new ProductRenderer();
        this.paginationRenderer = new PaginationRenderer();
        this.statsRenderer = new StatsRenderer();

        // UI modules
        this.chipsManager = new ChipsManager();
        this.loadingManager = new LoadingManager();
        this.urlManager = new UrlManager();
        this.filterUI = new FilterUI(this, this.chipsManager);

        // Set pagination callback
        this.paginationRenderer.setOnPageChange((page) => {
            this.handlePageChange(page);
        });
    }

    /**
     * Initialize
     * 
     * @returns {Promise<void>}
     */
    async init() {
        // Wait for SlimSelect to be initialized before loading filters from URL
        // SlimSelect initializes on DOMContentLoaded, so we wait a bit
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                // Wait a bit more for SlimSelect initialization
                setTimeout(() => {
                    this.loadFiltersFromUrl();
                }, 100);
            });
        } else {
            // DOM already loaded, wait a bit for SlimSelect
            setTimeout(() => {
                this.loadFiltersFromUrl();
            }, 100);
        }
        
        // Initialize active states
        this.filterUI.initializeActiveStates();

        // Update filter block state (for mobile button)
        this.updateActiveFiltersState();

        // Initialize product counts from existing DOM (for initial page load)
        this.initializeProductCounts();

        // Initialize archive description visibility based on current page from URL
        const currentPageFromUrl = this.urlManager.getCurrentPageFromUrl();
        this.toggleArchiveDescription(currentPageFromUrl);

        // Attach server pagination listener
        this.attachServerPaginationListener();

        // Initialize responsive pagination for server-rendered pagination
        this.paginationRenderer.applyResponsivePagination();
        this.paginationRenderer.setupResizeListener();

        // Setup WooCommerce sorting integration
        this.setupWooCommerceSorting();

        // Setup custom sorting integration
        this.setupCustomSorting();

        // Setup mobile sorting dropdown
        this.setupMobileSorting();

        // Setup filter form submission prevention
        this.setupFilterFormSubmission();
        
        // Setup infinite scroll if enabled
        if (this.options.infiniteScroll) {
            window.addEventListener('scroll', debounce(() => {
                this.checkInfiniteScroll();
            }, 100));
        }
        
        // Setup resize handler
        window.addEventListener('resize', debounce(() => {
            this.handleResize();
        }, 250));
    }

    /**
     * Handle filter change
     * 
     * @param {HTMLElement} element Changed element
     * @returns {void}
     */
    handleFilterChange(element) {
        console.log('[Filter] handleFilterChange called', {
            element: element,
            elementType: element.type,
            elementTagName: element.tagName,
            elementName: element.name,
            elementValue: element.value,
            isChecked: element.checked
        });
        
        // Get active filters
        const activeFields = this.filterManager.getActiveFiltersForAPI();
        
        // Update filter state
        this.filterState.setFilters(this.parseFiltersFromFields(activeFields));
        
        // Update current sort if sorting changed (radio or select)
        if (
            (element.type === 'radio' && element.name === 'apf_sorting' && element.checked) ||
            (element.tagName === 'SELECT' && element.name === 'apf_sorting')
        ) {
            console.log('[Filter] Sorting changed in filter', {
                element: element,
                selectedNode: element.tagName === 'SELECT' ? element.options[element.selectedIndex] : element
            });
            const selectedNode = (element.tagName === 'SELECT')
                ? element.options[element.selectedIndex]
                : element;
            const dataSet = selectedNode?.getAttribute('data-set');
            if (dataSet && dataSet.includes('type|sorting')) {
                const pairs = dataSet.split('|');
                const dataObj = {};
                for (let i = 0; i < pairs.length; i += 2) {
                    if (pairs[i + 1] !== undefined) {
                        dataObj[pairs[i]] = pairs[i + 1];
                    }
                }
                
                if (dataObj.orderby && dataObj.order) {
                    this.filterState.setSort({
                        orderBy: dataObj.orderby,
                        order: dataObj.order.toLowerCase()
                    });
                    this.currentSort = this.filterState.getSort();
                    
                    // Sync WooCommerce sorting select (skip event to prevent loop)
                    this.syncWooCommerceSorting(true);
                }
            }
        }

        // Update chips for price and range sliders
        this.updateSliderChips();
        
        // Update chips for select elements
        this.updateSelectChips();
        
        // Update Clear all chip visibility
        if (this.chipsManager) {
            this.chipsManager.updateClearAllChip(this.getFilterContainer());
        }

        // Update active filters state (for body class and mobile button)
        this.updateActiveFiltersState();

        // Reset pagination
        this.filterState.resetPage();
        this.currentPage = 1;

        // Don't apply filters if we're loading from URL (products are already loaded server-side)
        if (this.isLoadingFromUrl) {
            console.log('[Filter] Skipping filter application - loading from URL');
            return;
        }
        
        console.log('[Filter] About to call debounceFilter from handleFilterChange');
        // Apply filters with debounce
        this.debounceFilter();
    }

    /**
     * Parse filters from active fields
     * 
     * @param {Array<Object>} activeFields Active filter objects (from getActiveFiltersForAPI)
     * @returns {Object} Filters object
     */
    parseFiltersFromFields(activeFields) {
        const filters = {};
        activeFields.forEach(field => {
            // Field is already a processed object, not HTMLElement
            if (field.type) {
                const key = field.taxonomy || field.meta_key || field.type;
                if (key) {
                    filters[key] = field.value;
                }
            }
        });
        return filters;
    }

    /**
     * Debounce filter application
     * 
     * @returns {void}
     */
    debounceFilter() {
        console.log('[Filter] debounceFilter called');
        // For now, apply immediately (debounce can be re-enabled if needed)
        this.applyFilters();
    }

    /**
     * Apply filters
     * 
     * @returns {Promise<void>}
     */
    async applyFilters() {
        console.log('[Filter] applyFilters called', {
            isLoading: this.isLoading,
            currentSort: this.currentSort,
            stack: new Error().stack
        });
        
        if (this.isLoading) {
            console.log('[Filter] applyFilters blocked - isLoading is true');
            return;
        }

        // Show full-page overlay immediately (before setting isLoading)
        this.loadingManager.showFullPageOverlay();

        this.isLoading = true;
        this.loadingManager.show();

        try {
            // Check WooCommerce orderby select if sort is not set in filterState
            const currentSort = this.filterState.getSort();
            if (!currentSort || !currentSort.orderBy) {
                const wcSortingSelect = document.querySelector('.woocommerce-ordering select[name="orderby"]');
                if (wcSortingSelect && wcSortingSelect.value) {
                    // Parse WooCommerce orderby and set it
                    const sort = this.parseWooCommerceOrderby(wcSortingSelect.value);
                    if (sort && sort.orderBy) {
                        this.filterState.setSort(sort);
                        this.currentSort = sort;
                    }
                }
            }
            
            // Get active filters
            const activeFilters = this.filterManager.getActiveFiltersForAPI();
            
            // Get main query context from filter form
            const mainQueryContext = this.getMainQueryContext();
            
            // Build API configuration
            const config = this.configBuilder.build(activeFilters, {
                page: this.filterState.getPage(),
                perPage: this.options.perPage,
                orderBy: this.filterState.getSort()?.orderBy,
                order: this.filterState.getSort()?.order,
                currentUrl: window.location.origin + window.location.pathname,
                mainQueryContext: mainQueryContext
            });

            // Check cache - include order_by and order in options for cache key
            const cacheOptions = {
                ...config.options,
                order_by: config.order_by,
                order: config.order
            };
            const cacheKey = this.cacheManager.generateKey(activeFilters, cacheOptions);
            console.log('[Filter] Cache key generated', {
                cacheKey: cacheKey,
                order_by: config.order_by,
                order: config.order,
                options: cacheOptions
            });
            let results = await this.cacheManager.get(cacheKey);

            // If not in cache, fetch from API
            if (!results) {
                results = await this.apiClient.fetchProducts(config);
                
                // Save to cache
                await this.cacheManager.set(cacheKey, results);
            }

            // Store results for infinite scroll
            this.lastResults = results;
            
            // Render results first (products will be replaced)
            this.renderResults(results);
            
            // Update URL
            this.urlManager.update(activeFilters, {
                currentPage: this.filterState.getPage(),
                currentSort: this.filterState.getSort() || this.currentSort
            });

            // Sync WooCommerce sorting select after applying filters (skip event to prevent loop)
            this.syncWooCommerceSorting(true);

            // Update filter counts
            this.updateFilterCounts(results);

            // Update slider chips after successful filter application
            this.updateSliderChips();
            
            // Update select chips after successful filter application
            this.updateSelectChips();
            
            // Update Clear all chip visibility
            if (this.chipsManager) {
                this.chipsManager.updateClearAllChip(this.getFilterContainer());
            }

            // Update active filters state (for body class and mobile button)
            this.updateActiveFiltersState();

        } catch (error) {
            console.error('Chyba při filtrování:', error);
            this.productRenderer.showError('Chyba při načítání produktů');
        } finally {
            this.isLoading = false;
            // Hide overlay after products are rendered (or error is shown)
            this.loadingManager.hide();
            
            // Update available filter options after filters are applied (don't await to avoid blocking)
            // This runs asynchronously and doesn't block subsequent filter changes
            this.updateFilterOptions().catch(error => {
                // Silently fail - don't show error to user
            });
        }
    }

    /**
     * Render results
     * 
     * @param {Object} results API results
     * @returns {void}
     */
    renderResults(results) {
        // Clear container
        this.productRenderer.clear();

        if (results.total_count === 0) {
            this.productRenderer.showNoResults();
            return;
        }

        // Render products
        if (results.products && Array.isArray(results.products)) {
            this.productRenderer.render(results.products);
        } else if (results.html) {
            // Fallback for HTML format
            const container = this.productRenderer.getContainer();
            if (container) {
                container.innerHTML = results.html;
            }
        }

        // Render pagination
        this.paginationRenderer.render(results, {
            currentPage: this.filterState.getPage(),
            perPage: this.options.perPage,
            onPageChange: (page) => {
                this.handlePageChange(page);
            }
        });

        // Render stats
        this.statsRenderer.render(results);
    }

    /**
     * Handle page change
     *
     * @param {number} page Page number
     * @returns {void}
     */
    handlePageChange(page) {
        this.filterState.setPage(page);
        this.currentPage = page;
        this.applyFilters();

        // Scroll to top of products list
        this.scrollToProducts();

        // Toggle archive description visibility (show only on first page)
        this.toggleArchiveDescription(page);
    }

    /**
     * Scroll to the top of products list
     *
     * @returns {void}
     */
    scrollToProducts() {
        const productsContainer = this.productRenderer.getContainer();
        if (productsContainer) {
            // Get offset from top with some padding
            const offset = 20;
            const containerTop = productsContainer.getBoundingClientRect().top + window.pageYOffset - offset;

            window.scrollTo({
                top: containerTop,
                behavior: 'smooth'
            });
        }
    }

    /**
     * Toggle archive description visibility
     * Shows only on first page, hides on other pages
     *
     * @param {number} page Current page number
     * @returns {void}
     */
    toggleArchiveDescription(page) {
        const archiveDescription = document.querySelector('.archive-description-container');
        if (archiveDescription) {
            if (page === 1) {
                archiveDescription.style.display = '';
            } else {
                archiveDescription.style.display = 'none';
            }
        }
    }

    /**
     * Load filters from URL
     * 
     * @returns {void}
     */
    loadFiltersFromUrl() {
        // Set flag to prevent filter application during URL loading
        this.isLoadingFromUrl = true;
        
        const urlParams = new URLSearchParams(window.location.search);
        const filterRoot = document.querySelector('.affinite-product-filter');
        
        if (!filterRoot) {
            console.warn('No .affinite-product-filter container found for URL loading');
            this.isLoadingFromUrl = false;
            return;
        }

        // Get current page from URL
        const page = this.urlManager.getCurrentPageFromUrl();
        this.filterState.setPage(page);
        this.currentPage = page;

        // Load filters from URL (this is complex logic, keeping it for now)
        // TODO: Extract to UrlManager
        urlParams.forEach((value, key) => {
            if (key === 'categories') {
                this.setCategories(value, filterRoot);
            } else if (key === 'stock_status') {
                this.setStockStatus(value, filterRoot);
            } else if (key === 'price') {
                this.setPriceRange(value, filterRoot);
            } else if (key === 'orderby') {
                // Handle orderby from URL - can be from filter or WooCommerce
                this.handleOrderbyFromUrl(value, filterRoot);
            } else if (key.startsWith('pa_')) {
                this.setAttributeFilter(key, value, filterRoot);
            } else if (key !== 'page') {
                this.setMetaKeyFilter(key, value, filterRoot);
            }
        });

            // Wait a bit for all selects to be initialized and values set
            // This ensures SlimSelect is ready and values are set before updating chips
            setTimeout(() => {
                // Update slider chips after loading from URL
                this.updateSliderChips();
                
                // Update select chips after loading from URL
                this.updateSelectChips();
                
                // Update Clear all chip visibility
                if (this.chipsManager) {
                    this.chipsManager.updateClearAllChip(this.getFilterContainer());
                }

                // Update active filters state (for body class and mobile button)
                this.updateActiveFiltersState();
                
                // Reset flag - URL loading is complete
                // NOTE: We do NOT call applyFilters() here because products should be
                // loaded server-side for SEO. JavaScript only updates the UI state (chips, form values).
                this.isLoadingFromUrl = false;
            }, 200);

        // Sync WooCommerce sorting select after loading from URL (skip event to prevent loop)
        if (this.currentSort) {
            this.syncWooCommerceSorting(true);
        } else {
            // If no sort is set, check if there's a WooCommerce select and sync it
            const wcSortingSelect = document.querySelector('.woocommerce-ordering select[name="orderby"]');
            if (wcSortingSelect && wcSortingSelect.value) {
                // Parse WooCommerce orderby and set it
                const sort = this.parseWooCommerceOrderby(wcSortingSelect.value);
                this.filterState.setSort(sort);
                this.currentSort = sort;
            }
        }
    }

    /**
     * Set categories from URL
     * 
     * @param {string} value Category slugs
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    setCategories(value, filterRoot) {
        const categories = value.split(',');
        categories.forEach(slug => {
            const checkbox = filterRoot.querySelector(`input[type="checkbox"][data-set*="type|category"][data-set*="slug|${slug}"]`);
            if (checkbox) {
                checkbox.checked = true;
                checkbox.closest('.apf-checkbox__item, .apf-label__item, .apf-color__item, .apf-thumbnail__item')?.classList.add('active');
                this.chipsManager.createChipFromInput(checkbox);
            }
        });
    }

    /**
     * Set stock status from URL
     * 
     * @param {string} value Stock status value
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    setStockStatus(value, filterRoot) {
        const statuses = value.split(',');
        statuses.forEach(status => {
            const checkbox = filterRoot.querySelector(`input[type="checkbox"][data-set*="type|stock_status"][data-set*="value|${status}"]`);
            if (checkbox) {
                checkbox.checked = true;
                checkbox.closest('.apf-checkbox__item, .apf-label__item, .apf-color__item, .apf-thumbnail__item')?.classList.add('active');
                this.chipsManager.createChipFromInput(checkbox);
            }
        });
    }

    /**
     * Set price range from URL
     * 
     * @param {string} value Price range value
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    setPriceRange(value, filterRoot) {
        const [minVal, maxVal] = value.split(',');
        if (!minVal || !maxVal) return;

        // Find price range container (new noUiSlider structure)
        const priceContainer = filterRoot.querySelector('.apf-range[data-type="price"]');
        if (priceContainer) {
            const sliderElement = priceContainer.querySelector('.apf-range__slider');
            if (sliderElement && sliderElement.noUiSlider) {
                // Set noUiSlider values
                // If loading from URL, don't trigger 'set' event to prevent filter application
                if (this.isLoadingFromUrl) {
                    sliderElement.noUiSlider.set([parseFloat(minVal), parseFloat(maxVal)], false);
                } else {
                    sliderElement.noUiSlider.set([parseFloat(minVal), parseFloat(maxVal)]);
                }
            }
        } else {
            // Fallback for old structure
            const priceSlider = filterRoot.querySelector('.apf-price-slider');
            if (priceSlider) {
                const inputMin = priceSlider.querySelector('.apf-price-slider__input--min');
                const inputMax = priceSlider.querySelector('.apf-price-slider__input--max');
                
                if (inputMin && inputMax) {
                    inputMin.value = minVal;
                    inputMax.value = maxVal;
                    
                    // Trigger input events to update UI
                    inputMin.dispatchEvent(new Event('input', { bubbles: true }));
                    inputMax.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }
        }
    }

    /**
     * Setup filter form submission prevention
     * Prevents default form submission when clicking on filter options
     * 
     * @returns {void}
     */
    setupFilterFormSubmission() {
        // Use event delegation to handle all filter forms (including dynamically added ones)
        document.addEventListener('submit', (e) => {
            const filterForm = e.target.closest('.affinite-product-filter');
            if (filterForm) {
                console.log('[Filter] Form submit event caught on filter form', {
                    target: e.target,
                    defaultPrevented: e.defaultPrevented
                });
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
            }
        }, { capture: true, passive: false });
        
        // Also prevent form submission by removing action attribute if present
        // and setting onsubmit handler for all filter forms
        const filterForms = document.querySelectorAll('.affinite-product-filter');
        filterForms.forEach((form) => {
            if (form.hasAttribute('action')) {
                form.removeAttribute('action');
            }
            if (!form.dataset.affiniteSubmitPrevented) {
                form.onsubmit = function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                };
                form.dataset.affiniteSubmitPrevented = '1';
            }
        });
        
        console.log('[Filter] Submit event prevention setup complete');
    }

    /**
     * Setup WooCommerce sorting integration
     * 
     * @returns {void}
     */
    setupWooCommerceSorting() {
        // Use event delegation on the form to handle dynamically replaced selects
        const wcSortingForm = document.querySelector('form.woocommerce-ordering');
        if (!wcSortingForm) {
            return;
        }

        // Prevent default form submission (only add once)
        // Use capture phase to catch event before other handlers
        if (!wcSortingForm.dataset.affiniteListenerAttached) {
            wcSortingForm.addEventListener('submit', (e) => {
                console.log('[WooCommerce] Form submit event caught', {
                    target: e.target,
                    defaultPrevented: e.defaultPrevented
                });
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
            }, { capture: true, once: false, passive: false });
            wcSortingForm.dataset.affiniteListenerAttached = '1';
            console.log('[WooCommerce] Submit event listener attached with capture phase');
        }

        // Use event delegation for change events (works even if select is replaced)
        // Use capture phase to catch event before other handlers
        if (!wcSortingForm.dataset.affiniteChangeListenerAttached) {
            wcSortingForm.addEventListener('change', (e) => {
                console.log('[WooCommerce] Change event caught on form', {
                    target: e.target,
                    targetName: e.target?.name,
                    targetValue: e.target?.value,
                    skipChange: e.target?.dataset?.affiniteSkipChange
                });
                
                const select = e.target;
                if (select && select.name === 'orderby' && select.closest('form.woocommerce-ordering')) {
                    // Skip if this change was triggered programmatically
                    if (select.dataset.affiniteSkipChange) {
                        console.log('[WooCommerce] Skipping change - affiniteSkipChange flag set');
                        return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    this.handleWooCommerceSortingChange(select);
                } else {
                    console.log('[WooCommerce] Change event not for orderby select', {
                        selectName: select?.name,
                        isInForm: select?.closest('form.woocommerce-ordering')
                    });
                }
            }, { capture: true, once: false, passive: false });
            wcSortingForm.dataset.affiniteChangeListenerAttached = '1';
            console.log('[WooCommerce] Change event listener attached with capture phase');
        } else {
            console.log('[WooCommerce] Change event listener already attached');
        }
        
        // Also prevent form submission by removing action and method attributes
        if (wcSortingForm.hasAttribute('action')) {
            wcSortingForm.removeAttribute('action');
            console.log('[WooCommerce] Removed action attribute from form');
        }
        if (wcSortingForm.method && wcSortingForm.method.toLowerCase() === 'get') {
            // Keep method as GET but prevent submission
            wcSortingForm.onsubmit = function(e) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            };
            console.log('[WooCommerce] Set onsubmit handler on form');
        }
    }

    /**
     * Setup custom sorting integration
     * Handles clicks on .custom-product-sorting-item elements
     *
     * @returns {void}
     */
    setupCustomSorting() {
        document.addEventListener('click', (e) => {
            const sortingItem = e.target.closest('.custom-product-sorting-item');
            if (!sortingItem) {
                return;
            }

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            const sortingValue = sortingItem.getAttribute('data-sorting');
            if (!sortingValue) {
                console.log('[CustomSorting] No data-sorting value found');
                return;
            }

            // Check if clicking on already active item - toggle off
            if (sortingItem.classList.contains('active')) {
                console.log('[CustomSorting] Deactivating sorting:', sortingValue);

                // Remove active class
                sortingItem.classList.remove('active');

                // Reset to default sort
                this.currentSort = null;
                this.filterState.setSort(null);

                // Apply filters without sort (will use default)
                this.applyFilters();
                return;
            }

            console.log('[CustomSorting] Sorting clicked:', sortingValue);

            // Map custom sorting values to orderBy/order
            let sort;
            switch (sortingValue) {
                case 'best-selling':
                    sort = { orderBy: 'popularity', order: 'desc' };
                    break;
                case 'price':
                    sort = { orderBy: 'price', order: 'asc' };
                    break;
                case 'price-desc':
                    sort = { orderBy: 'price', order: 'desc' };
                    break;
                default:
                    console.log('[CustomSorting] Unknown sorting value:', sortingValue);
                    return;
            }

            // Update current sort
            this.currentSort = sort;
            this.filterState.setSort(sort);

            // Update active state on sorting items
            document.querySelectorAll('.custom-product-sorting-item').forEach(item => {
                item.classList.remove('active');
            });
            sortingItem.classList.add('active');

            // Apply filters with new sort
            this.applyFilters();
        }, { capture: true });
    }

    /**
     * Setup mobile sorting dropdown
     * Handles the mobile sorting dropdown toggle and selection
     *
     * @returns {void}
     */
    setupMobileSorting() {
        const texts = window.affiniteProductFilterTexts || {};

        // Initialize from URL parameter
        this.initializeMobileSortingFromUrl();

        // Toggle dropdown open/close
        document.addEventListener('click', (e) => {
            const header = e.target.closest('.affinite-mobile-sorting__header');
            if (header) {
                e.preventDefault();
                e.stopPropagation();
                const dropdown = header.closest('.affinite-mobile-sorting');
                if (dropdown) {
                    dropdown.classList.toggle('open');
                }
                return;
            }

            // Handle option selection
            const option = e.target.closest('.affinite-mobile-sorting__option');
            if (option) {
                e.preventDefault();
                e.stopPropagation();

                const sortingValue = option.getAttribute('data-sorting');
                if (!sortingValue) {
                    return;
                }

                const dropdown = option.closest('.affinite-mobile-sorting');
                const label = dropdown?.querySelector('.affinite-mobile-sorting__label');

                // Update label text
                if (label) {
                    label.innerHTML = `${texts.sortingPlaceholder || 'Sorting by'}: <span class="affinite-mobile-sorting__value">${option.textContent}</span>`;
                }

                // Update active state
                dropdown?.querySelectorAll('.affinite-mobile-sorting__option').forEach(opt => {
                    opt.classList.remove('active');
                });
                option.classList.add('active');

                // Close dropdown
                dropdown?.classList.remove('open');

                // Map sorting value to sort object
                let sort;
                switch (sortingValue) {
                    case 'best-selling':
                        sort = { orderBy: 'popularity', order: 'desc' };
                        break;
                    case 'price':
                        sort = { orderBy: 'price', order: 'asc' };
                        break;
                    case 'price-desc':
                        sort = { orderBy: 'price', order: 'desc' };
                        break;
                    default:
                        return;
                }

                // Update current sort and apply filters
                this.currentSort = sort;
                this.filterState.setSort(sort);

                // Also update custom-product-sorting-item if exists
                document.querySelectorAll('.custom-product-sorting-item').forEach(item => {
                    item.classList.remove('active');
                    if (item.getAttribute('data-sorting') === sortingValue) {
                        item.classList.add('active');
                    }
                });

                this.applyFilters();
                return;
            }

            // Close dropdown when clicking outside
            const openDropdown = document.querySelector('.affinite-mobile-sorting.open');
            if (openDropdown && !e.target.closest('.affinite-mobile-sorting')) {
                openDropdown.classList.remove('open');
            }
        });
    }

    /**
     * Initialize mobile sorting dropdown from URL parameter
     * Checks for orderby parameter and pre-selects the corresponding option
     *
     * @returns {void}
     */
    initializeMobileSortingFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const orderby = urlParams.get('orderby');

        if (!orderby) {
            return;
        }

        // Map URL orderby values to data-sorting values
        let sortingValue;
        switch (orderby) {
            case 'popularity':
                sortingValue = 'best-selling';
                break;
            case 'price':
                sortingValue = 'price';
                break;
            case 'price-desc':
                sortingValue = 'price-desc';
                break;
            default:
                return;
        }

        const dropdown = document.querySelector('.affinite-mobile-sorting');
        if (!dropdown) {
            return;
        }

        const option = dropdown.querySelector(`.affinite-mobile-sorting__option[data-sorting="${sortingValue}"]`);
        if (!option) {
            return;
        }

        const label = dropdown.querySelector('.affinite-mobile-sorting__label');
        const texts = window.affiniteProductFilterTexts || {};

        // Update label text
        if (label) {
            label.innerHTML = `${texts.sortingPlaceholder || 'Sorting by'}: <span class="affinite-mobile-sorting__value">${option.textContent}</span>`;
        }

        // Update active state
        dropdown.querySelectorAll('.affinite-mobile-sorting__option').forEach(opt => {
            opt.classList.remove('active');
        });
        option.classList.add('active');
    }

    /**
     * Handle WooCommerce sorting change
     * 
     * @param {HTMLElement} selectElement WooCommerce sorting select element
     * @returns {void}
     */
    handleWooCommerceSortingChange(selectElement) {
        console.log('[WooCommerce] handleWooCommerceSortingChange called', {
            selectElement: selectElement,
            value: selectElement.value
        });
        
        const orderbyValue = selectElement.value;
        if (!orderbyValue) {
            console.log('[WooCommerce] No orderby value, returning');
            return;
        }

        // Parse WooCommerce orderby to orderBy/order format
        const sort = this.parseWooCommerceOrderby(orderbyValue);
        console.log('[WooCommerce] Parsed sort:', sort);
        
        // Set sort state
        this.filterState.setSort(sort);
        this.currentSort = sort;

        // Update active filters state (for body class and mobile button)
        this.updateActiveFiltersState();

        // Reset pagination
        this.filterState.resetPage();
        this.currentPage = 1;

        console.log('[WooCommerce] About to call debounceFilter from handleWooCommerceSortingChange');
        // Apply filters with debounce (same as handleFilterChange)
        this.debounceFilter();
    }

    /**
     * Convert orderBy/order to WooCommerce orderby value
     * 
     * @param {Object} sort Sort object { orderBy, order }
     * @returns {string} WooCommerce orderby value
     */
    convertSortToWooCommerceOrderby(sort) {
        if (!sort || !sort.orderBy) {
            return 'menu_order';
        }

        const { orderBy, order } = sort;
        const orderLower = (order || 'asc').toLowerCase();

        if (orderBy === 'price' && orderLower === 'desc') {
            return 'price-desc';
        } else if (orderBy === 'price' && orderLower === 'asc') {
            return 'price';
        } else if (orderBy === 'menu_order' && orderLower === 'asc') {
            return 'menu_order';
        } else if (orderBy === 'popularity' && orderLower === 'desc') {
            return 'popularity';
        } else if (orderBy === 'rating' && orderLower === 'desc') {
            return 'rating';
        } else if (orderBy === 'date' && orderLower === 'desc') {
            return 'date';
        }

        // Default fallback
        return 'menu_order';
    }

    /**
     * Sync WooCommerce sorting select with current sort
     * 
     * @param {boolean} skipEvent Skip triggering change event (to prevent infinite loop)
     * @returns {void}
     */
    syncWooCommerceSorting(skipEvent = false) {
        const wcSortingSelect = document.querySelector('.woocommerce-ordering select[name="orderby"]');
        if (!wcSortingSelect || !this.currentSort) {
            return;
        }

        const orderbyValue = this.convertSortToWooCommerceOrderby(this.currentSort);
        if (wcSortingSelect.value !== orderbyValue) {
            // With event delegation, we can safely set the value
            // The change event will be caught by the form listener, but we can prevent it
            if (skipEvent) {
                // Temporarily disable the event listener by setting a flag
                wcSortingSelect.dataset.affiniteSkipChange = '1';
                wcSortingSelect.value = orderbyValue;
                // Remove flag after a short delay
                setTimeout(() => {
                    delete wcSortingSelect.dataset.affiniteSkipChange;
                }, 100);
            } else {
                wcSortingSelect.value = orderbyValue;
            }
        }
    }

    /**
     * Handle orderby from URL (can be from filter or WooCommerce)
     * 
     * @param {string} value Orderby value from URL
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    handleOrderbyFromUrl(value, filterRoot) {
        if (!value) {
            return;
        }

        // First try to find matching filter sorting option
        const sortingRadio = filterRoot.querySelector(`input[type="radio"][name="apf_sorting"][value="${value}"]`);
        if (sortingRadio) {
            // Found matching filter sorting option
            this.setSorting(value, filterRoot);
            // Sync WooCommerce select
            this.syncWooCommerceSorting();
            return;
        }

        // If not found in filter, try WooCommerce orderby values
        const wcOrderbyValues = ['menu_order', 'popularity', 'rating', 'date', 'price', 'price-desc'];
        if (wcOrderbyValues.includes(value)) {
            // Parse WooCommerce orderby
            const sort = this.parseWooCommerceOrderby(value);
            this.filterState.setSort(sort);
            this.currentSort = sort;
            
            // Sync WooCommerce select (skip event to prevent loop)
            const wcSortingSelect = document.querySelector('.woocommerce-ordering select[name="orderby"]');
            if (wcSortingSelect && wcSortingSelect.value !== value) {
                wcSortingSelect.dataset.affiniteSkipChange = '1';
                wcSortingSelect.value = value;
                // Remove flag after a short delay
                setTimeout(() => {
                    delete wcSortingSelect.dataset.affiniteSkipChange;
                }, 100);
            }
            
            // Try to sync filter sorting if possible
            // Find filter sorting option that matches the orderBy/order
            const filterSortingOptions = filterRoot.querySelectorAll('input[type="radio"][name="apf_sorting"]');
            for (const option of filterSortingOptions) {
                const dataSet = option.getAttribute('data-set');
                if (dataSet) {
                    const pairs = dataSet.split('|');
                    const dataObj = {};
                    for (let i = 0; i < pairs.length; i += 2) {
                        if (pairs[i + 1] !== undefined) {
                            dataObj[pairs[i]] = pairs[i + 1];
                        }
                    }
                    
                    if (dataObj.orderby === sort.orderBy && 
                        dataObj.order && 
                        dataObj.order.toLowerCase() === sort.order.toLowerCase()) {
                        option.checked = true;
                        // Update active class
                        const sortingItems = filterRoot.querySelectorAll('.apf-sorting__item');
                        sortingItems.forEach(item => item.classList.remove('active'));
                        const sortingItem = option.closest('.apf-sorting__item');
                        if (sortingItem) {
                            sortingItem.classList.add('active');
                        }
                        break;
                    }
                }
            }
        }
    }

    /**
     * Set sorting from URL
     * 
     * @param {string} value Sorting value
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    setSorting(value, filterRoot) {
        const sortingRadio = filterRoot.querySelector(`input[type="radio"][name="apf_sorting"][value="${value}"]`);
        if (sortingRadio) {
            sortingRadio.checked = true;
            const dataSet = sortingRadio.getAttribute('data-set');
            if (dataSet) {
                const pairs = dataSet.split('|');
                const dataObj = {};
                for (let i = 0; i < pairs.length; i += 2) {
                    if (pairs[i + 1] !== undefined) {
                        dataObj[pairs[i]] = pairs[i + 1];
                    }
                }
                
                if (dataObj.orderby && dataObj.order) {
                    this.filterState.setSort({
                        orderBy: dataObj.orderby,
                        order: dataObj.order.toLowerCase()
                    });
                    this.currentSort = this.filterState.getSort();
                }
            }
            
            // Update active class
            const sortingItems = filterRoot.querySelectorAll('.apf-sorting__item');
            sortingItems.forEach(item => item.classList.remove('active'));
            const sortingItem = sortingRadio.closest('.apf-sorting__item');
            if (sortingItem) {
                sortingItem.classList.add('active');
            }
        }
    }

    /**
     * Set attribute filter from URL
     * 
     * @param {string} key Attribute taxonomy
     * @param {string} value Attribute values
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    setAttributeFilter(key, value, filterRoot) {
        // Check if a range slider exists for this key before treating comma as range
        if (value.includes(',')) {
            const rangeContainer = filterRoot.querySelector(`.apf-range[data-taxonomy="${key}"], .apf-range[data-meta_key="${key}"]`);
            if (rangeContainer) {
                this.setRangeSlider(key, value, filterRoot);
                return;
            }
        }

        {
            // Single or multiple values - can be checkbox or select
            const values = value.split(',');
            
            // First try to find select element for this taxonomy
            // Component key is directly the taxonomy slug (from SelectRenderer::getComponentKey)
            // For attributes: wc_sanitize_taxonomy_name($attribute) which returns the taxonomy slug (e.g., pa_color)
            // For custom taxonomies: taxonomy slug directly
            let select = null;
            let component = null;
            
            // Component key is directly the taxonomy slug
            component = filterRoot.querySelector(`[data-component-key="${key}"]`);
            if (component) {
                select = component.querySelector('select.apf-filter__select, .apf-filter__select select');
            }
            
            if (select) {
                // This is a select filter
                // First, try to find options by value or by data attributes
                const validValues = [];
                values.forEach(val => {
                    // Try to find option by value
                    let option = Array.from(select.options).find(opt => opt.value === val);
                    
                    // If not found by value, try to find by data-set attribute (slug or term_id)
                    if (!option) {
                        option = Array.from(select.options).find(opt => {
                            const dataSet = opt.getAttribute('data-set');
                            if (dataSet) {
                                // Check if data-set contains the value
                                return dataSet.includes(`slug|${val}`) || 
                                       dataSet.includes(`term_id|${val}`) ||
                                       dataSet.includes(`value|${val}`);
                            }
                            return false;
                        });
                    }
                    
                    if (option) {
                        validValues.push(option.value);
                    }
                });
                
                if (validValues.length > 0) {
                    // For multi-select (SlimSelect), set all valid values
                    if (select.slim && select.slim.set) {
                        select.slim.set(validValues);
                    } else {
                        // Single select - set first valid value
                        select.value = validValues[0];
                    }
                    
                    // Wait a bit for SlimSelect to update, then update chips
                    // Don't dispatch change event during URL loading to prevent filter application
                    setTimeout(() => {
                        // Only dispatch change event if NOT loading from URL
                        // This ensures chips are updated without triggering filter application
                        if (!this.isLoadingFromUrl) {
                            const changeEvent = new Event('change', { bubbles: true });
                            select.dispatchEvent(changeEvent);
                        }
                        
                        // Always update chips manually after setting value
                        this.updateSelectChips();
                    }, 100);
                }
            } else {
                // Try to find checkboxes
                values.forEach(slug => {
                    // Try to find checkbox by slug first
                    let checkbox = filterRoot.querySelector(`input[type="checkbox"][data-set*="type|attribute"][data-set*="taxonomy|${key}"][data-set*="slug|${slug}"]`);
                    
                    // If not found by slug, try to find by term_id (in case slug is actually a term_id)
                    if (!checkbox && !isNaN(slug)) {
                        checkbox = filterRoot.querySelector(`input[type="checkbox"][data-set*="type|attribute"][data-set*="taxonomy|${key}"][data-set*="term_id|${slug}"]`);
                    }
                    
                    if (checkbox) {
                        checkbox.checked = true;
                        checkbox.closest('.apf-checkbox__item, .apf-label__item, .apf-color__item, .apf-thumbnail__item')?.classList.add('active');
                        this.chipsManager.createChipFromInput(checkbox);
                    }
                });
            }
        }
    }

    /**
     * Set meta key filter from URL
     * 
     * @param {string} key Meta key
     * @param {string} value Meta values
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    setMetaKeyFilter(key, value, filterRoot) {
        // Check if a range slider exists for this key before treating comma as range
        if (value.includes(',')) {
            const rangeContainer = filterRoot.querySelector(`.apf-range[data-taxonomy="${key}"], .apf-range[data-meta_key="${key}"]`);
            if (rangeContainer) {
                this.setRangeSlider(key, value, filterRoot);
                return;
            }
        }

        {
            // Single or multiple values - can be checkbox or select
            const values = value.split(',');
            
            // First try to find select element for this meta_key
            // Component key is directly the meta_key (from SelectRenderer::getComponentKey)
            let select = null;
            let component = null;
            
            // Component key is directly the meta_key
            component = filterRoot.querySelector(`[data-component-key="${key}"]`);
            if (component) {
                select = component.querySelector('select.apf-filter__select, .apf-filter__select select');
            }
            
            if (select) {
                // This is a select filter
                // For multi-select (SlimSelect), set all values
                if (select.slim && select.slim.set) {
                    select.slim.set(values);
                } else {
                    // Single select - set first value
                    if (values.length > 0) {
                        select.value = values[0];
                    }
                }
                
                // Wait a bit for SlimSelect to update, then update chips
                // Don't dispatch change event during URL loading to prevent filter application
                setTimeout(() => {
                    // Only dispatch change event if NOT loading from URL
                    // This ensures chips are updated without triggering filter application
                    if (!this.isLoadingFromUrl) {
                        const changeEvent = new Event('change', { bubbles: true });
                        select.dispatchEvent(changeEvent);
                    }
                    
                    // Always update chips manually after setting value
                    this.updateSelectChips();
                }, 100);
            } else {
                // Try to find checkboxes
                values.forEach(val => {
                    const checkbox = filterRoot.querySelector(`input[type="checkbox"][data-set*="type|meta_key"][data-set*="meta_key|${key}"][data-set*="value|${val}"]`);
                    if (checkbox) {
                        checkbox.checked = true;
                        checkbox.closest('.apf-checkbox__item, .apf-label__item, .apf-color__item, .apf-thumbnail__item')?.classList.add('active');
                        this.chipsManager.createChipFromInput(checkbox);
                    }
                });
            }
        }
    }

    /**
     * Set range slider values from URL
     * 
     * @param {string} key Taxonomy or meta_key
     * @param {string} value Range values (min,max)
     * @param {HTMLElement} filterRoot Filter root
     * @returns {void}
     */
    setRangeSlider(key, value, filterRoot) {
        const [minVal, maxVal] = value.split(',');
        if (!minVal || !maxVal) {
            console.warn(`[setRangeSlider] Invalid range values: ${value}`);
            return;
        }

        console.log(`[setRangeSlider] Setting range for key: ${key}, values: ${minVal}, ${maxVal}`);

        // Try new noUiSlider structure first (.apf-range)
        const rangeContainer = filterRoot.querySelector(`.apf-range[data-taxonomy="${key}"], .apf-range[data-meta_key="${key}"]`);
        if (rangeContainer) {
            const sliderElement = rangeContainer.querySelector('.apf-range__slider');
            if (sliderElement && sliderElement.noUiSlider) {
                const type = rangeContainer.dataset.type;
                const isDiscrete = rangeContainer.classList.contains('apf-range--discrete');
                
                if (isDiscrete) {
                    // For discrete ranges, need to find indices in values array
                    let values = [];
                    try {
                        values = JSON.parse(rangeContainer.dataset.values || '[]');
                    } catch (e) {
                        console.warn('[setRangeSlider] Failed to parse data-values for discrete range');
                        return;
                    }
                    
                    const minIndex = values.indexOf(minVal);
                    const maxIndex = values.indexOf(maxVal);
                    
                    if (minIndex === -1 || maxIndex === -1) {
                        console.warn(`[setRangeSlider] Values ${minVal}, ${maxVal} not found in slider data-values`);
                        return;
                    }
                    
                    // Set noUiSlider values using indices
                    // If loading from URL, don't trigger 'set' event to prevent filter application
                    if (this.isLoadingFromUrl) {
                        sliderElement.noUiSlider.set([minIndex, maxIndex], false);
                    } else {
                        sliderElement.noUiSlider.set([minIndex, maxIndex]);
                    }
                    // Update dataset values
                    rangeContainer.dataset.valuemin = minVal;
                    rangeContainer.dataset.valuemax = maxVal;
                } else {
                    // For continuous ranges (price, numeric), use values directly
                    const minNum = parseFloat(minVal);
                    const maxNum = parseFloat(maxVal);
                    if (!isNaN(minNum) && !isNaN(maxNum)) {
                        // If loading from URL, don't trigger 'set' event to prevent filter application
                        if (this.isLoadingFromUrl) {
                            sliderElement.noUiSlider.set([minNum, maxNum], false);
                        } else {
                            sliderElement.noUiSlider.set([minNum, maxNum]);
                        }
                        // Update dataset values
                        rangeContainer.dataset.valuemin = String(Math.round(minNum));
                        rangeContainer.dataset.valuemax = String(Math.round(maxNum));
                    }
                }
                
                console.log(`[setRangeSlider] Successfully set noUiSlider values for ${key}`);
                return;
            }
        }

        // Fallback to old structure (input[type="range"])
        const rangeInputs = filterRoot.querySelectorAll('input[type="range"]');
        let targetSlider = null;
        let targetSliderId = null;

        // Find the slider that matches this key
        rangeInputs.forEach(input => {
            const dataSet = this.filterManager.getObjectFromDataSet(input);
            if ((dataSet.taxonomy === key) || (dataSet.meta_key === key)) {
                if (!targetSlider) {
                    targetSlider = input.closest('.apf-range-slider');
                    targetSliderId = dataSet.slider_id;
                }
            }
        });

        if (!targetSlider || !targetSliderId) {
            console.warn(`[setRangeSlider] Range slider not found for key: ${key}`);
            return;
        }

        // Get data-values to find indices
        let values = [];
        if (targetSlider.dataset.values) {
            try {
                values = JSON.parse(targetSlider.dataset.values);
            } catch (e) {
                console.warn('[setRangeSlider] Failed to parse data-values for range slider');
                return;
            }
        }

        // Find indices for min and max values
        const minIndex = values.indexOf(minVal);
        const maxIndex = values.indexOf(maxVal);

        if (minIndex === -1 || maxIndex === -1) {
            console.warn(`[setRangeSlider] Values ${minVal}, ${maxVal} not found in slider data-values`);
            return;
        }

        // Set slider values
        const inputMin = targetSlider.querySelector('.apf-range-slider__input--min');
        const inputMax = targetSlider.querySelector('.apf-range-slider__input--max');

        if (inputMin && inputMax) {
            inputMin.value = minIndex;
            inputMax.value = maxIndex;
            
            // Trigger input events to update UI
            inputMin.dispatchEvent(new Event('input', { bubbles: true }));
            inputMax.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    /**
     * Attach server pagination listener
     * 
     * @returns {void}
     */
    attachServerPaginationListener() {
        // This is handled by PaginationRenderer
    }

    /**
     * Update filter counts
     * 
     * @param {Object} results API results
     * @returns {void}
     */
    updateFilterCounts(results) {
        if (!results.filter_counts) {
            return;
        }

        // Update counts for each filter type
        Object.entries(results.filter_counts).forEach(([filterType, counts]) => {
            this.updateFilterTypeCounts(filterType, counts);
        });
    }

    /**
     * Update counts for specific filter type
     * 
     * @param {string} filterType Filter type
     * @param {Object} counts Counts object
     * @returns {void}
     */
    updateFilterTypeCounts(filterType, counts) {
        // Find all elements of this filter type
        const filterElements = document.querySelectorAll(`[data-affinite-filter="${filterType}"]`);
        
        filterElements.forEach(element => {
            this.updateFilterElementCounts(element, counts);
        });
    }

    /**
     * Update counts for specific filter element
     * 
     * @param {HTMLElement} element Filter element
     * @param {Object} counts Counts object
     * @returns {void}
     */
    updateFilterElementCounts(element, counts) {
        // For checkbox filters
        const checkboxes = element.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
            const value = checkbox.value;
            const count = counts[value];
            
            if (count !== undefined) {
                // Find label or span for displaying count
                const label = checkbox.closest('.apf-checkbox__item, .apf-label__item, .apf-color__item');
                if (label) {
                    let countElement = label.querySelector('.filter-count');
                    if (!countElement) {
                        countElement = document.createElement('span');
                        countElement.className = 'filter-count';
                        label.appendChild(countElement);
                    }
                    countElement.textContent = `(${count})`;
                    
                    // Hide option if count is 0
                    if (count === 0) {
                        label.style.opacity = '0.5';
                        label.style.pointerEvents = 'none';
                    } else {
                        label.style.opacity = '1';
                        label.style.pointerEvents = 'auto';
                    }
                }
            }
        });
    }

    /**
     * Get posts per page
     * 
     * @returns {number} Posts per page
     */
    getPostsPerPage() {
        return this.options.perPage;
    }

    /**
     * Get default sorting
     * 
     * @returns {Object|null} Default sort object
     */
    getDefaultSorting() {
        // Check for default sorting in DOM
        const defaultSorting = document.querySelector('input[type="radio"][name="apf_sorting"][value="default"]');
        if (defaultSorting) {
            const dataSet = defaultSorting.getAttribute('data-set');
            if (dataSet) {
                const pairs = dataSet.split('|');
                const dataObj = {};
                for (let i = 0; i < pairs.length; i += 2) {
                    if (pairs[i + 1] !== undefined) {
                        dataObj[pairs[i]] = pairs[i + 1];
                    }
                }
                
                if (dataObj.orderby && dataObj.order) {
                    return {
                        orderBy: dataObj.orderby,
                        order: dataObj.order.toLowerCase()
                    };
                }
            }
        }
        
        return {
            orderBy: 'date',
            order: 'desc'
        };
    }

    /**
     * Debug logging
     * 
     * @param {string} message Log message
     * @param {Object|null} data Log data
     * @returns {Promise<void>}
     */
    async debugLogToFile(message, data = null) {
        const timestamp = new Date().toISOString();
        const logEntry = {
            timestamp: timestamp,
            message: message,
            data: data
        };
        
        this.debugLog.push(logEntry);
        
        // Limit log entries
        if (this.debugLog.length > this.maxLogEntries) {
            this.debugLog.shift();
        }
        
        // Console backup
        console.log(`[DEBUG] ${message}`, data);
        
        // Try to write to file via WordPress AJAX
        try {
            const logData = {
                action: 'affinite_debug_log',
                message: message,
                data: JSON.stringify(data),
                timestamp: timestamp,
                nonce: window.affiniteProductFilterTexts?.nonce || ''
            };
            
            fetch(this.options.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(logData)
            }).catch(err => {
                console.warn('Failed to write debug log:', err);
            });
        } catch (err) {
            console.warn('Failed to setup debug logging:', err);
        }
    }

    /**
     * Download debug log
     * 
     * @returns {void}
     */
    downloadDebugLog() {
        const logText = this.debugLog.map(entry => {
            return `[${entry.timestamp}] ${entry.message}\n${entry.data ? JSON.stringify(entry.data, null, 2) : ''}\n---\n`;
        }).join('\n');
        
        const blob = new Blob([logText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `affinite-debug-${new Date().toISOString().slice(0, 19)}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    // Legacy methods for backward compatibility
    getActiveFields() {
        return this.filterManager.getActiveFields();
    }

    getActiveFieldsForAPI() {
        return this.filterManager.getActiveFiltersForAPI();
    }

    async fetchResults(filters, options = {}) {
        // This is now handled by applyFilters, but keeping for compatibility
        return await this.applyFilters();
    }

    updateUrl() {
        const activeFilters = this.filterManager.getActiveFiltersForAPI();
        this.urlManager.update(activeFilters, {
            currentPage: this.filterState.getPage(),
            currentSort: this.filterState.getSort()
        });
    }

    renderProducts(products, container) {
        this.productRenderer.render(products);
    }

    renderPagination(results) {
        this.paginationRenderer.render(results, {
            currentPage: this.filterState.getPage(),
            perPage: this.options.perPage,
            onPageChange: (page) => {
                this.handlePageChange(page);
            }
        });
    }

    renderStats(results) {
        this.statsRenderer.render(results);
    }

    /**
     * Initialize product counts from existing DOM elements
     * Used on initial page load before any AJAX filtering
     * Updates all .product-counts elements (desktop and mobile)
     *
     * @returns {void}
     */
    initializeProductCounts() {
        const productCountsElements = document.querySelectorAll('.product-counts');
        if (!productCountsElements.length) {
            return;
        }

        // Count products currently on page
        const productsContainer = this.productRenderer.getContainer();
        const currentProducts = productsContainer
            ? productsContainer.querySelectorAll('.product, .type-product').length
            : 0;

        // Try to get total from WooCommerce result count or data attribute
        let totalProducts = currentProducts;

        // Try to extract total from .woocommerce-result-count text
        const wcResultCount = document.querySelector('.woocommerce-result-count');
        if (wcResultCount) {
            const text = wcResultCount.textContent;
            // Match patterns like "Showing 1–12 of 137 results" or "Zobrazuji 1–12 ze 137 výsledků"
            const match = text.match(/(\d+)\s*(?:results|výsledků|produktů|products)/i);
            if (match) {
                totalProducts = parseInt(match[1], 10);
            } else {
                // Try another pattern "of X" or "ze X"
                const ofMatch = text.match(/(?:of|ze|z)\s*(\d+)/i);
                if (ofMatch) {
                    totalProducts = parseInt(ofMatch[1], 10);
                }
            }
        }

        // Build the count text
        const texts = window.affiniteProductFilterTexts || {};
        let countText;

        if (totalProducts === 0) {
            countText = texts.noProductsFound || 'No products found';
        } else if (totalProducts === 1) {
            countText = texts.productCountSingle || '1 product';
        } else if (currentProducts >= totalProducts) {
            countText = texts.productCountPlural
                ? texts.productCountPlural.replace('%s', totalProducts)
                : `${totalProducts} products`;
        } else {
            countText = texts.productCountFromTotal
                ? texts.productCountFromTotal.replace('%1$s', currentProducts).replace('%2$s', totalProducts)
                : `${currentProducts} from ${totalProducts} products`;
        }

        // Update all product counts elements
        productCountsElements.forEach(element => {
            element.textContent = countText;
        });
    }

    showLoading() {
        this.loadingManager.show();
    }

    hideLoading() {
        this.loadingManager.hide();
    }

    showNoResults() {
        this.productRenderer.showNoResults();
    }

    showError(message) {
        this.productRenderer.showError(message);
    }

    createChip(filterRoot, targetId, targetLabel, targetSlug) {
        this.chipsManager.createChip(filterRoot, targetId, targetLabel, targetSlug);
    }

    createChipFromInput(input, filterRoot) {
        this.chipsManager.createChipFromInput(input);
    }

    getResultsContainer() {
        return this.productRenderer.getContainer();
    }

    getPaginationContainers() {
        return this.paginationRenderer.getContainers();
    }

    getStatsContainer() {
        return this.statsRenderer.getStatsContainer();
    }

    getFilterContainer() {
        return document.querySelector('.affinite-product-filter');
    }

    /**
     * Get filter name from container's title element
     *
     * @param {HTMLElement} container Filter component container
     * @returns {string} Filter name or empty string
     */
    getFilterNameFromContainer(container) {
        if (!container) {
            return '';
        }

        // Find parent filter component wrapper (.apf-filter) which contains the title
        // Note: We specifically look for .apf-filter first, as other elements like .apf-range
        // are children of .apf-filter and don't contain the title themselves
        const component = container.closest('.apf-filter, [data-component-key]') || container;

        // Try to get title from h3.apf-filter__title
        const titleElement = component.querySelector('.apf-filter__title');
        if (titleElement) {
            // Get text content without child elements (like count span)
            const clone = titleElement.cloneNode(true);
            const countSpan = clone.querySelector('.apf-filter__count');
            if (countSpan) countSpan.remove();
            return clone.textContent.trim();
        }

        return '';
    }

    /**
     * Get main query context from filter form data attribute
     *
     * @returns {Object|null} Main query context or null
     */
    getMainQueryContext() {
        const filterRoot = this.getFilterContainer();
        if (!filterRoot) {
            return null;
        }

        const contextAttr = filterRoot.getAttribute('data-main-query-context');
        if (!contextAttr) {
            return null;
        }

        try {
            return JSON.parse(contextAttr);
        } catch (e) {
            console.warn('Failed to parse main query context:', e);
            return null;
        }
    }

    /**
     * Update filter block state (add body class and show/hide mobile button)
     * 
     * @returns {void}
     */
    updateActiveFiltersState() {
        const filterRoot = this.getFilterContainer();
        const hasFilterBlock = filterRoot !== null;
        
        // Add/remove body class if filter block exists
        if (hasFilterBlock) {
            document.body.classList.add('affinite-filter-block-exists');
        } else {
            document.body.classList.remove('affinite-filter-block-exists');
        }

        // Show/hide mobile filter toggle button
        const mobileToggle = document.querySelector('.affinite-mobile-filter-toggle');
        if (mobileToggle) {
            if (hasFilterBlock) {
                mobileToggle.style.display = '';
            } else {
                mobileToggle.style.display = 'none';
            }
        }
    }

    /**
     * Update chips for price and range sliders
     * 
     * @returns {void}
     */
    updateSliderChips() {
        const filterRoot = this.getFilterContainer();
        if (!filterRoot) {
            return;
        }

        // Process all range sliders (price and discrete ranges)
        const rangeContainers = filterRoot.querySelectorAll('.apf-range');
        rangeContainers.forEach(container => {
            const sliderId = container.dataset.id || container.dataset.sliderId || 'range';
            const type = container.dataset.type || 'range';
            const isDiscrete = container.classList.contains('apf-range--discrete');
            const min = parseFloat(container.dataset.min || '0');
            const max = parseFloat(container.dataset.max || '100');
            
            // Get current values from dataset (may be undefined if slider is at default)
            const valueminRaw = container.dataset.valuemin;
            const valuemaxRaw = container.dataset.valuemax;
            
            // If values are not set, slider is at default - skip chip creation
            if (typeof valueminRaw === 'undefined' || typeof valuemaxRaw === 'undefined') {
                // Remove chip if exists - use chipsManager to find container (may be outside filterRoot)
                const chips = this.chipsManager.getChipsContainer(filterRoot);
                if (chips) {
                    const existingChip = chips.querySelector(`.affinite-product-filter__chip[data-slider-id="${sliderId}"]`);
                    if (existingChip) {
                        existingChip.remove();
                    }
                }
                return;
            }
            
            // For discrete ranges, compare raw string values
            // For continuous ranges (price), compare numeric values
            let valuemin, valuemax;
            let isAtDefault = false;
            
            if (isDiscrete) {
                // For discrete ranges, compare string values directly
                valuemin = valueminRaw;
                valuemax = valuemaxRaw;
                
                // Get first and last values from values array to determine default range
                try {
                    const values = JSON.parse(container.dataset.values || '[]');
                    if (values.length > 0) {
                        const firstValue = values[0];
                        const lastValue = values[values.length - 1];
                        // Check if current values match the full range (default)
                        isAtDefault = (valuemin === firstValue && valuemax === lastValue);
                    }
                } catch (e) {
                    // If can't parse, fall back to numeric comparison
                    valuemin = parseFloat(valueminRaw) || min;
                    valuemax = parseFloat(valuemaxRaw) || max;
                    isAtDefault = (valuemin === min && valuemax === max);
                }
            } else {
                // For continuous ranges (price), compare numeric values
                valuemin = parseFloat(valueminRaw) || min;
                valuemax = parseFloat(valuemaxRaw) || max;
                isAtDefault = (valuemin === min && valuemax === max);
            }

            // Check if values are different from defaults
            if (!isAtDefault) {
                let chipLabel = '';
                
                if (type === 'price') {
                    // Price slider - format with currency using WooCommerce settings
                    const minFormatted = formatWooCommercePrice(valuemin, false);
                    const maxFormatted = formatWooCommercePrice(valuemax, false);
                    chipLabel = `${minFormatted} - ${maxFormatted}`;
                } else {
                    // Discrete range slider - use labels if available
                    if (isDiscrete) {
                        try {
                            const values = JSON.parse(container.dataset.values || '[]');
                            const labels = JSON.parse(container.dataset.labels || '[]');
                            
                            // For discrete ranges, valuemin/valuemax are string slugs, not indices
                            // Find indices in values array
                            const minIdx = values.indexOf(valuemin);
                            const maxIdx = values.indexOf(valuemax);
                            
                            if (minIdx !== -1 && maxIdx !== -1) {
                                const minLabel = labels[minIdx] || values[minIdx] || valuemin;
                                const maxLabel = labels[maxIdx] || values[maxIdx] || valuemax;
                                chipLabel = `${minLabel} - ${maxLabel}`;
                            } else {
                                // Fallback: use raw values
                                chipLabel = `${valuemin} - ${valuemax}`;
                            }
                        } catch (e) {
                            chipLabel = `${valuemin} - ${valuemax}`;
                        }
                    } else {
                        chipLabel = `${valuemin} - ${valuemax}`;
                    }
                }

                // Create or update chip
                // For discrete ranges, pass string values; for continuous, pass numeric
                const minValueStr = isDiscrete ? String(valuemin) : String(Math.round(valuemin));
                const maxValueStr = isDiscrete ? String(valuemax) : String(Math.round(valuemax));

                // Get filter name from container's title element
                const filterName = this.getFilterNameFromContainer(container);

                this.chipsManager.createSliderChip(
                    filterRoot,
                    sliderId,
                    type,
                    chipLabel,
                    minValueStr,
                    maxValueStr,
                    filterName
                );
            } else {
                // Remove chip if values are at defaults
                // Use chipsManager to find chips container (may be outside filterRoot)
                const chips = this.chipsManager.getChipsContainer(filterRoot);
                if (chips) {
                    const existingChip = chips.querySelector(`.affinite-product-filter__chip[data-slider-id="${sliderId}"]`);
                    if (existingChip) {
                        existingChip.remove();
                    }
                }
            }
        });
    }

    /**
     * Update chips for select elements
     * 
     * @returns {void}
     */
    updateSelectChips() {
        const filterRoot = this.getFilterContainer();
        if (!filterRoot) {
            return;
        }

        // Process all select elements (excluding sorting)
        const selects = filterRoot.querySelectorAll('select.apf-filter__select, .apf-filter__select select');
        selects.forEach(select => {
            // Skip sorting selects
            if (select.name === 'apf_sorting' || select.getAttribute('data-set')?.includes('type|sorting')) {
                return;
            }

            // Get component key for unique identification
            const component = select.closest('.apf-filter');
            const componentKey = component?.dataset.componentKey;
            const selectId = componentKey || select.id || select.name;
            
            if (!selectId) {
                return; // Skip if no selectId found
            }
            
            // Get selected values from SlimSelect if available (multi-select)
            let selectedValues = [];
            if (select.slim && select.slim.selected) {
                // SlimSelect multi-select - get all selected values
                selectedValues = select.slim.selected();
            } else if (select.value !== '') {
                // Single select or native select
                selectedValues = [select.value];
            }
            
            // Remove all existing chips for this select
            // Find all chips that start with this selectId (format: ${selectId}_${value})
            const chips = this.chipsManager.getChipsContainer(filterRoot);
            if (chips) {
                // Get all chips with data-select-id attribute
                const allChips = chips.querySelectorAll('.affinite-product-filter__chip[data-select-id]');
                allChips.forEach(chip => {
                    const chipSelectId = chip.getAttribute('data-select-id');
                    // Check if this chip belongs to this select (starts with selectId_)
                    if (chipSelectId && chipSelectId.startsWith(selectId + '_')) {
                        chip.remove();
                    }
                });
            }
            
            // Create chip for each selected value
            if (selectedValues.length > 0) {
                // Get filter name from component
                const filterName = this.getFilterNameFromContainer(component || select);

                selectedValues.forEach(value => {
                    // Find the option with this value
                    const option = Array.from(select.options).find(opt => opt.value === value);
                    if (option) {
                        // Get label from option
                        const label = option.textContent || option.getAttribute('data-label') || value;

                        // Create chip with unique ID combining selectId and value
                        const chipId = `${selectId}_${value}`;
                        if (label) {
                            this.chipsManager.createSelectChip(filterRoot, chipId, label, value, filterName);
                        }
                    }
                });
            }
        });
    }

    clearFilters() {
        const filterRoot = this.getFilterContainer();
        if (!filterRoot) {
            return;
        }
        
        // Reset filter state
        this.filterState.reset();
        this.currentFilters = {};
        this.currentPage = 1;
        this.currentSort = null;
        
        // Reset all checkboxes
        const checkboxes = filterRoot.querySelectorAll('input[type="checkbox"]:checked');
        checkboxes.forEach(checkbox => {
            checkbox.checked = false;
            const item = checkbox.closest('.apf-filter__item, .apf-checkbox__item, .apf-label__item, .apf-color__item, .apf-thumbnail__item');
            if (item) {
                item.classList.remove('active');
            }
        });
        
        // Reset all selects
        const selects = filterRoot.querySelectorAll('select');
        selects.forEach(select => {
            if (select.value !== '') {
                select.value = '';
                // Trigger change event for SlimSelect if present
                if (select.slim) {
                    select.slim.set('');
                }
            }
        });

        // Reset all range sliders
        const rangeContainers = filterRoot.querySelectorAll('.apf-range');
        rangeContainers.forEach(container => {
            const sliderElement = container.querySelector('.apf-range__slider');
            if (sliderElement && sliderElement.noUiSlider) {
                const min = parseFloat(container.dataset.min || '0');
                const max = parseFloat(container.dataset.max || '100');
                const isDiscrete = container.classList.contains('apf-range--discrete');
                
                // Remove dataset values
                delete container.dataset.valuemin;
                delete container.dataset.valuemax;
                
                // Determine default slider values
                let defaultMin, defaultMax;
                if (isDiscrete) {
                    try {
                        const values = JSON.parse(container.dataset.values || '[]');
                        if (values.length > 0) {
                            defaultMin = 0;
                            defaultMax = values.length - 1;
                        } else {
                            defaultMin = min;
                            defaultMax = max;
                        }
                    } catch (e) {
                        defaultMin = min;
                        defaultMax = max;
                    }
                } else {
                    defaultMin = min;
                    defaultMax = max;
                }
                
                // Reset slider without triggering events
                sliderElement.noUiSlider.set([defaultMin, defaultMax], false);
            }
        });
        
        // Clear all chips
        if (this.chipsManager) {
            this.chipsManager.clear(filterRoot);
        }
        
        // Reset form
        const form = filterRoot.closest('form');
        if (form) {
            form.reset();
        }
        
        // Apply filters to refresh results
        this.applyFilters();
    }

    /**
     * Restore filters from URL (used when closing mobile filter without applying)
     * This method resets UI state first, then loads filters from current URL
     *
     * @returns {void}
     */
    restoreFiltersFromUrl() {
        const filterRoot = this.getFilterContainer();
        if (!filterRoot) {
            return;
        }

        // Reset filter state (but don't apply yet)
        this.filterState.reset();
        this.currentFilters = {};
        this.currentPage = 1;
        this.currentSort = null;

        // Reset all checkboxes
        const checkboxes = filterRoot.querySelectorAll('input[type="checkbox"]:checked');
        checkboxes.forEach(checkbox => {
            checkbox.checked = false;
            const item = checkbox.closest('.apf-filter__item, .apf-checkbox__item, .apf-label__item, .apf-color__item, .apf-thumbnail__item');
            if (item) {
                item.classList.remove('active');
            }
        });

        // Reset all selects
        const selects = filterRoot.querySelectorAll('select');
        selects.forEach(select => {
            if (select.value !== '') {
                select.value = '';
                // Trigger change event for SlimSelect if present
                if (select.slim) {
                    select.slim.set('');
                }
            }
        });

        // Reset all range sliders to defaults
        const rangeContainers = filterRoot.querySelectorAll('.apf-range');
        rangeContainers.forEach(container => {
            const sliderElement = container.querySelector('.apf-range__slider');
            if (sliderElement && sliderElement.noUiSlider) {
                const min = parseFloat(container.dataset.min || '0');
                const max = parseFloat(container.dataset.max || '100');
                const isDiscrete = container.classList.contains('apf-range--discrete');

                // Remove dataset values
                delete container.dataset.valuemin;
                delete container.dataset.valuemax;

                // Determine default slider values
                let defaultMin, defaultMax;
                if (isDiscrete) {
                    try {
                        const values = JSON.parse(container.dataset.values || '[]');
                        if (values.length > 0) {
                            defaultMin = 0;
                            defaultMax = values.length - 1;
                        } else {
                            defaultMin = min;
                            defaultMax = max;
                        }
                    } catch (e) {
                        defaultMin = min;
                        defaultMax = max;
                    }
                } else {
                    defaultMin = min;
                    defaultMax = max;
                }

                // Reset slider without triggering events
                sliderElement.noUiSlider.set([defaultMin, defaultMax], false);
            }
        });

        // Clear all chips
        if (this.chipsManager) {
            this.chipsManager.clear(filterRoot);
        }

        // Now load filters from URL (which has been updated to original)
        this.loadFiltersFromUrl();

        // Apply filters to fetch products
        this.applyFilters();
    }

    /**
     * Handle sort change
     * 
     * @param {string} sortValue Sort value
     * @returns {void}
     */
    handleSortChange(sortValue) {
        const sort = this.parseSortingOption(sortValue);
        if (sort) {
            this.filterState.setSort(sort);
            this.currentSort = sort;
        }
        this.filterState.resetPage();
        this.currentPage = 1;
        this.applyFilters();
    }

    /**
     * Parse sorting option
     * 
     * @param {string} sortValue Sort value
     * @returns {Object|null} Sort object
     */
    parseSortingOption(sortValue) {
        const sortingRadio = document.querySelector(`input[type="radio"][name="apf_sorting"][value="${sortValue}"]`);
        if (sortingRadio) {
            const dataSet = sortingRadio.getAttribute('data-set');
            if (dataSet) {
                const pairs = dataSet.split('|');
                const dataObj = {};
                for (let i = 0; i < pairs.length; i += 2) {
                    if (pairs[i + 1] !== undefined) {
                        dataObj[pairs[i]] = pairs[i + 1];
                    }
                }
                
                if (dataObj.orderby && dataObj.order) {
                    return {
                        orderBy: dataObj.orderby,
                        order: dataObj.order.toLowerCase()
                    };
                }
            }
        }
        return null;
    }

    /**
     * Parse WooCommerce orderby value to orderBy/order format
     * 
     * @param {string} orderbyValue WooCommerce orderby value (menu_order, popularity, rating, date, price, price-desc)
     * @returns {Object} Sort object { orderBy, order }
     */
    parseWooCommerceOrderby(orderbyValue) {
        switch (orderbyValue) {
            case 'menu_order':
                return { orderBy: 'menu_order', order: 'asc' };
            case 'popularity':
                return { orderBy: 'popularity', order: 'desc' };
            case 'rating':
                return { orderBy: 'rating', order: 'desc' };
            case 'date':
                return { orderBy: 'date', order: 'desc' };
            case 'price':
                return { orderBy: 'price', order: 'asc' };
            case 'price-desc':
                return { orderBy: 'price', order: 'desc' };
            default:
                return { orderBy: 'date', order: 'desc' };
        }
    }

    /**
     * Check infinite scroll
     * 
     * @returns {void}
     */
    checkInfiniteScroll() {
        if (!this.options.infiniteScroll) {
            return;
        }

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const documentHeight = document.documentElement.scrollHeight;

        // Check if user is near bottom of page
        if (scrollTop + windowHeight >= documentHeight - 100) {
            // Check if there are more pages
            const totalPages = Math.ceil((this.lastResults?.total_count || 0) / this.options.perPage);
            const currentPage = this.filterState.getPage();
            
            if (currentPage < totalPages && !this.isLoading) {
                this.filterState.setPage(currentPage + 1);
                this.currentPage = currentPage + 1;
                this.applyFilters();
            }
        }
    }

    /**
     * Handle resize
     * 
     * @returns {void}
     */
    handleResize() {
        // Recalculate layout if needed
        this.updateLayout();
    }

    /**
     * Update layout
     * 
     * @returns {void}
     */
    updateLayout() {
        // Implementation as needed
    }

    /**
     * Update available filter options based on current filters
     * 
     * @returns {Promise<void>}
     */
    async updateFilterOptions() {
        const filterRoot = document.querySelector('.affinite-product-filter');
        if (!filterRoot) {
            return;
        }

        const filterId = filterRoot.dataset.filterId;
        if (!filterId) {
            return; // No filter ID, skip dynamic filtering
        }

        try {
            // Zrušit předchozí požadavek (race condition prevention)
            if (this.availableOptionsAbortController) {
                this.availableOptionsAbortController.abort();
            }
            this.availableOptionsAbortController = new AbortController();

            // Get current active filters
            const activeFields = this.filterManager.getActiveFiltersForAPI();

            // Call API to get available options
            const apiUrl = window.affiniteProductFilterTexts?.restUrl + 'filter/available-options' || '/wp-json/affinite-product-filter/v1/filter/available-options';
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: this.availableOptionsAbortController.signal,
                body: JSON.stringify({
                    fields: activeFields,
                    filter_id: parseInt(filterId),
                    current_url: window.location.origin + window.location.pathname
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (!data.success || !data.data) {
                return;
            }

            // Update each component
            const availableOptions = data.data;
            const components = filterRoot.querySelectorAll('[data-component-key]');

            components.forEach(component => {
                const componentKey = component.dataset.componentKey;

                if (!componentKey || !availableOptions[componentKey]) {
                    return;
                }

                const options = availableOptions[componentKey];
                this.updateComponentOptions(component, options);
            });

        } catch (error) {
            if (error.name === 'AbortError') {
                return; // Zrušený požadavek - ignorovat
            }
            // Silently fail - don't show error to user
        }
    }

    /**
     * Show or hide "no options available" message
     * 
     * @param {HTMLElement} component Component element
     * @param {boolean} show Whether to show the message
     * @returns {void}
     */
    toggleNoOptionsMessage(component, show) {
        // Find or create message element
        let messageEl = component.querySelector('.apf-filter__no-options');
        
        if (show) {
            if (!messageEl) {
                messageEl = document.createElement('div');
                messageEl.className = 'apf-filter__no-options';
                messageEl.textContent = window.affiniteProductFilterTexts?.noOptionsAvailable || 'Nejsou dostupné žádné možnosti';
                
                // Insert after title or at the beginning of component
                const title = component.querySelector('.apf-filter__title');
                if (title) {
                    title.insertAdjacentElement('afterend', messageEl);
                } else {
                    component.insertBefore(messageEl, component.firstChild);
                }
            }
            messageEl.style.display = '';
            
            // Hide filter controls
            const controls = component.querySelectorAll('.apf-filter__list, .apf-filter__control, .apf-range');
            controls.forEach(control => {
                control.style.display = 'none';
            });
        } else {
            if (messageEl) {
                messageEl.style.display = 'none';
            }
            
            // Show filter controls
            const controls = component.querySelectorAll('.apf-filter__list, .apf-filter__control, .apf-range');
            controls.forEach(control => {
                control.style.display = '';
            });
        }
    }

    /**
     * Update options for a specific component
     * 
     * @param {HTMLElement} component Component element
     * @param {Array} availableOptions Available options from API
     * @returns {void}
     */
    updateComponentOptions(component, availableOptions) {
        // Získat componentKey z komponenty (použít pro všechny typy)
        const componentKey = component.dataset.componentKey || '';
        
        // Check if it's a price slider (availableOptions is an object with min/max)
        if (componentKey.startsWith('price_')) {
            // Handle price slider
            if (availableOptions && typeof availableOptions === 'object' && 'min' in availableOptions && 'max' in availableOptions) {
                const newMin = parseFloat(availableOptions.min) || 0;
                const newMax = parseFloat(availableOptions.max) || 0;
                
                // Check if no options available (both min and max are 0)
                const hasNoOptions = newMin === 0 && newMax === 0;
                this.toggleNoOptionsMessage(component, hasNoOptions);
                
                if (hasNoOptions) {
                    return;
                }
                
                const priceRange = component.querySelector('.apf-range[data-type="price"]');
                if (priceRange) {
                    const sliderElement = priceRange.querySelector('.apf-range__slider');
                    if (sliderElement && sliderElement.noUiSlider) {
                        // Update data attributes
                        priceRange.dataset.min = newMin.toString();
                        priceRange.dataset.max = newMax.toString();
                        
                        // Handle case when min and max are the same
                        // noUiSlider requires min < max for range, so we add a small tolerance if needed
                        let rangeMin = newMin;
                        let rangeMax = newMax;
                        if (rangeMin === rangeMax) {
                            if (rangeMin > 0) {
                                // If they're the same and > 0, add small tolerance for range
                                rangeMin = Math.max(0, rangeMin - 1);
                                rangeMax = rangeMax + 1;
                            } else {
                                // If both are 0, set a small range
                                rangeMin = 0;
                                rangeMax = 1;
                            }
                        }
                        
                        // Update slider range (requires min < max)
                        sliderElement.noUiSlider.updateOptions({
                            range: {
                                min: rangeMin,
                                max: rangeMax
                            }
                        });
                        
                        // Set current values (can use same values - noUiSlider handles this)
                        // If min === max, both handles will be positioned at the same spot
                        sliderElement.noUiSlider.set([newMin, newMax], false);
                    }
                }
            } else {
                this.toggleNoOptionsMessage(component, true);
            }
            return; // Price slider handled, exit early
        }
        
        // For other components, availableOptions should be an array
        if (!Array.isArray(availableOptions)) {
            this.toggleNoOptionsMessage(component, true);
            return;
        }
        
        // Zkontrolovat, zda je componentKey přítomen (už máme z předchozí deklarace)
        if (!componentKey) {
            this.toggleNoOptionsMessage(component, true);
            return;
        }
        
        // Check if there are any available options
        const hasAvailableOptions = availableOptions.length > 0 && availableOptions.some(opt => {
            const value = opt.value || opt.term_slug || opt.meta_value;
            return value !== null && value !== undefined && value !== '';
        });
        
        // Show/hide no options message
        this.toggleNoOptionsMessage(component, !hasAvailableOptions);
        
        if (!hasAvailableOptions) {
            return;
        }
        
        // Vytvořit Map s klíči "componentKey:value" pro unikátní identifikaci
        // Použít Map místo Set, abychom mohli ukládat i další data (count, available)
        const availableOptionsMap = new Map();
        availableOptions
            .filter(opt => opt.available !== false)
            .forEach(opt => {
                const value = opt.value || opt.term_slug || opt.meta_value;
                if (value) {
                    const key = `${componentKey}:${value}`;
                    availableOptionsMap.set(key, {
                        value: value,
                        count: opt.count,
                        available: opt.available
                    });
                }
            });

        // UNIVERZÁLNÍ ZPRACOVÁNÍ: Najít všechny .apf-filter__item v komponentě
        const items = component.querySelectorAll('.apf-filter__item');
        
        let availableItemsCount = 0;
        
        items.forEach(item => {
            // Najít input (checkbox nebo radio)
            const input = item.querySelector('input[type="checkbox"], input[type="radio"]');
            if (!input) {
                return; // Přeskočit, pokud není input (např. pro select)
            }

            // Získat hodnotu z input.value nebo input.dataset.slug
            const value = input.value || input.dataset.slug;
            
            // Vytvořit kombinovaný klíč "componentKey:value"
            const key = `${componentKey}:${value}`;
            const optionData = availableOptionsMap.get(key);
            const isAvailable = !!optionData;

            // Označit jako dostupnou nebo nedostupnou
            if (isAvailable) {
                // Make item available
                item.style.display = '';
                item.classList.remove('apf-filter__item--unavailable');
                input.disabled = false;
                input.removeAttribute('data-unavailable');
                availableItemsCount++;
                
                // Update count if available (pro všechny typy)
                const countEl = item.querySelector('.checkbox__item-count, .radio__item-count, .thumbnail__item-count, .color__item-count');
                if (countEl && optionData.count !== undefined) {
                    countEl.textContent = optionData.count;
                }
            } else {
                // Mark item as unavailable (but keep it visible)
                item.style.display = '';
                item.classList.add('apf-filter__item--unavailable');
                input.disabled = true;
                input.setAttribute('data-unavailable', 'true');
                
                // Uncheck if it was checked
                if (input.checked) {
                    input.checked = false;
                    // Trigger change event to update filters
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        });
        
        // If no items are available, show message
        if (availableItemsCount === 0 && items.length > 0) {
            this.toggleNoOptionsMessage(component, true);
        }

        // Zpracování select elementů
        const select = component.querySelector('.apf-filter__select');
        if (select) {
            let availableOptionsCount = 0;
            const options = select.querySelectorAll('option');
            options.forEach(option => {
                if (option.value === '') {
                    return; // Keep placeholder
                }

                const value = option.value;
                const key = `${componentKey}:${value}`;
                const optionData = availableOptionsMap.get(key);
                const isAvailable = !!optionData;

                // Instead of hiding, mark as disabled
                if (isAvailable) {
                    option.style.display = '';
                    option.disabled = false;
                    option.removeAttribute('data-unavailable');
                    availableOptionsCount++;
                    
                    // Update count if available
                    if (optionData && optionData.count !== undefined) {
                        option.dataset.count = optionData.count;
                    }
                } else {
                    // Mark option as disabled (but keep it visible)
                    option.style.display = '';
                    option.disabled = true;
                    option.setAttribute('data-unavailable', 'true');
                    
                    // If this option was selected, reset to placeholder
                    if (option.selected) {
                        const placeholder = select.querySelector('option[value=""]');
                        if (placeholder) {
                            placeholder.selected = true;
                            // Trigger change event to update filters
                            select.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }
                }
            });
            
            // If no options are available (except placeholder), show message
            if (availableOptionsCount === 0) {
                this.toggleNoOptionsMessage(component, true);
            }

            // If SlimSelect is initialized, destroy and re-initialize it
            if (select.slim) {
                select.slim.destroy();
                // Remove enhanced flag to allow re-initialization
                delete select.dataset.enhanced;
                // Re-initialize SlimSelect
                if (window.initSlimSelectForElement && typeof window.initSlimSelectForElement === 'function') {
                    window.initSlimSelectForElement(select);
                } else {
                    // Fallback: try to initialize directly if helper function is not available
                    try {
                        if (typeof SlimSelect !== 'undefined') {
                            new SlimSelect({
                                select: select,
                                settings: {
                                    showSearch: (select.dataset.type === 'sorting') ? false : true,
                                    placeholderText: select.getAttribute('placeholder') || ''
                                }
                            });
                            select.dataset.enhanced = '1';
                        }
                    } catch (e) {
                        // Silently fail
                    }
                }
            }
        }
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    window.affiniteProductFilter = new AffiniteProductFilter({
        cacheEnabled: (window.affiniteProductFilterOptions && typeof window.affiniteProductFilterOptions.cacheEnabled !== 'undefined')
            ? window.affiniteProductFilterOptions.cacheEnabled !== false
            : true,
        cacheTime: (window.affiniteProductFilterOptions && typeof window.affiniteProductFilterOptions.cacheTime !== 'undefined')
            ? parseInt(window.affiniteProductFilterOptions.cacheTime, 10)
            : (5 * 60 * 1000),
        debounceTime: 300,
        infiniteScroll: true,
        updateUrl: true
    });
    
    // Clear all filters button
    document.addEventListener('click', function(e) {
        if (e.target.matches('.affinite-product-filter__clear-all-button') || 
            e.target.closest('.affinite-product-filter__clear-all-button')) {
            e.preventDefault();
            const button = e.target.closest('.affinite-product-filter__clear-all-button') || e.target;
            if (window.affiniteProductFilter && window.affiniteProductFilter.clearFilters) {
                window.affiniteProductFilter.clearFilters();
            }
        }
    });
    
    // Generic filter select change listener (includes sorting)
    document.addEventListener('change', function(e) {
        const target = e.target;
        console.log('[Global] Change event on document', {
            target: target,
            matches: target?.matches('.apf-filter__select'),
            name: target?.name,
            value: target?.value
        });
        
        // Handle select elements (both native and SlimSelect enhanced)
        if (target && (target.tagName === 'SELECT' || target.matches('.apf-filter__select'))) {
            // If it's a wrapper, find the actual select element
            const selectElement = target.tagName === 'SELECT' ? target : target.querySelector('select');
            if (selectElement) {
                console.log('[Global] Calling handleFilterChange for select');
                if (window.affiniteProductFilter && window.affiniteProductFilter.handleFilterChange) {
                    window.affiniteProductFilter.handleFilterChange(selectElement);
                } else {
                    console.error('[Global] affiniteProductFilter or handleFilterChange not available');
                }
            }
        }
    });

    // Helper function to toggle mobile filter panel
    function toggleMobileFilterPanel(open) {
        const filterContainer = document.querySelector('.affinite-product-filter');
        const toggleButton = document.querySelector('.affinite-mobile-filter-toggle__button');
        const overlay = document.querySelector('.affinite-mobile-filter-overlay');
        
        if (filterContainer) {
            let isOpen = false;
            
            if (open === undefined) {
                // Toggle
                filterContainer.classList.toggle('affinite-product-filter--mobile-open');
                isOpen = filterContainer.classList.contains('affinite-product-filter--mobile-open');
            } else if (open) {
                // Open
                filterContainer.classList.add('affinite-product-filter--mobile-open');
                isOpen = true;
            } else {
                // Close
                filterContainer.classList.remove('affinite-product-filter--mobile-open');
                isOpen = false;
            }
            
            // Show/hide overlay
            if (overlay) {
                if (isOpen) {
                    overlay.classList.add('affinite-mobile-filter-overlay--active');
                } else {
                    overlay.classList.remove('affinite-mobile-filter-overlay--active');
                }
            }
            
            // Block/unblock page scroll
            if (isOpen) {
                // Save current scroll position
                const scrollY = window.scrollY;
                document.body.setAttribute('data-scroll-y', scrollY.toString());
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                document.body.style.width = '100%';
                document.body.classList.add('affinite-filter-panel-open');
            } else {
                // Restore scroll position
                const scrollY = document.body.getAttribute('data-scroll-y');
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.classList.remove('affinite-filter-panel-open');
                document.body.removeAttribute('data-scroll-y');
                
                if (scrollY) {
                    window.scrollTo(0, parseInt(scrollY, 10));
                }
            }
            
            // Update button state
            if (toggleButton) {
                if (isOpen) {
                    toggleButton.classList.add('affinite-mobile-filter-toggle__button--active');
                } else {
                    toggleButton.classList.remove('affinite-mobile-filter-toggle__button--active');
                }
            }
        }
    }

    // Mobile filter toggle button
    document.addEventListener('click', function(e) {
        const target = e.target;
        if (target && (target.matches('.affinite-mobile-filter-toggle__button') || target.closest('.affinite-mobile-filter-toggle__button'))) {
            e.preventDefault();
            const button = target.closest('.affinite-mobile-filter-toggle__button') || target;
            const action = button.getAttribute('data-action');

            if (action === 'toggle-filters') {
                // Save current URL before opening mobile filter panel
                const closeButton = document.querySelector('.affinite-product-filter__mobile-header-close');
                if (closeButton) {
                    closeButton.setAttribute('data-original-url', window.location.href);
                }
                toggleMobileFilterPanel();
            }
        }

        // Mobile header close button - restore original URL
        if (target && (target.matches('.affinite-product-filter__mobile-header-close') || target.closest('.affinite-product-filter__mobile-header-close'))) {
            e.preventDefault();
            const closeButton = target.closest('.affinite-product-filter__mobile-header-close') || target;
            const originalUrl = closeButton.getAttribute('data-original-url');

            toggleMobileFilterPanel(false);

            // Restore original URL if it exists and is different from current
            if (originalUrl && originalUrl !== window.location.href) {
                window.history.replaceState({}, '', originalUrl);
                // Reset UI and reload filters from original URL
                if (window.affiniteProductFilter) {
                    window.affiniteProductFilter.restoreFiltersFromUrl();
                }
            }
        }

        // Close panel when clicking on overlay - restore original URL
        if (target && target.matches('.affinite-mobile-filter-overlay')) {
            e.preventDefault();
            const closeButton = document.querySelector('.affinite-product-filter__mobile-header-close');
            const originalUrl = closeButton ? closeButton.getAttribute('data-original-url') : null;

            toggleMobileFilterPanel(false);

            // Restore original URL if it exists and is different from current
            if (originalUrl && originalUrl !== window.location.href) {
                window.history.replaceState({}, '', originalUrl);
                // Reset UI and reload filters from original URL
                if (window.affiniteProductFilter) {
                    window.affiniteProductFilter.restoreFiltersFromUrl();
                }
            }
        }

        // Save filters button - just closes the panel (filters are already applied, keep current URL)
        if (target && (target.matches('.affinite-product-filter__save-button') || target.closest('.affinite-product-filter__save-button'))) {
            e.preventDefault();
            // Clear the saved original URL so it won't be restored
            const closeButton = document.querySelector('.affinite-product-filter__mobile-header-close');
            if (closeButton) {
                closeButton.removeAttribute('data-original-url');
            }
            toggleMobileFilterPanel(false);
        }

        // Close button (bottom) - same behavior as mobile header close (restore original URL)
        if (target && (target.matches('.affinite-product-filter__close-button') || target.closest('.affinite-product-filter__close-button'))) {
            e.preventDefault();
            const headerCloseButton = document.querySelector('.affinite-product-filter__mobile-header-close');
            const originalUrl = headerCloseButton ? headerCloseButton.getAttribute('data-original-url') : null;

            toggleMobileFilterPanel(false);

            // Restore original URL if it exists and is different from current
            if (originalUrl && originalUrl !== window.location.href) {
                window.history.replaceState({}, '', originalUrl);
                // Reset UI and reload filters from original URL
                if (window.affiniteProductFilter) {
                    window.affiniteProductFilter.restoreFiltersFromUrl();
                }
            }
        }

        // Reset filters link - clear all filters and sorting
        if (target && (target.matches('.affinite-product-filter__reset-link') || target.closest('.affinite-product-filter__reset-link'))) {
            e.preventDefault();
            if (window.affiniteProductFilter) {
                // Clear the saved original URL
                const headerCloseButton = document.querySelector('.affinite-product-filter__mobile-header-close');
                if (headerCloseButton) {
                    headerCloseButton.removeAttribute('data-original-url');
                }
                // Clear all filters (this also applies the empty filter state)
                window.affiniteProductFilter.clearFilters();
            }
        }
    });
    
    // Global function for downloading debug log
    window.downloadAffiniteDebugLog = function() {
        if (window.affiniteProductFilter) {
            window.affiniteProductFilter.downloadDebugLog();
        } else {
            console.error('affiniteProductFilter not initialized');
        }
    };
});

// Export for modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = AffiniteProductFilter;
}

// Price and Range Slider initialization (from original file)
// This code initializes price and range sliders on page load
document.addEventListener('DOMContentLoaded', function() {
    const filterRoot = document.querySelector('.affinite-product-filter');
    if (!filterRoot) return;

    // Initialize noUiSlider for price and range
    const sliders = filterRoot.querySelectorAll('.apf-range__slider');
    sliders.forEach(sl => {
        const container = sl.closest('.apf-range');
        if (!container || sl.noUiSlider) return;
        const min = parseFloat(container.dataset.min || '0');
        const max = parseFloat(container.dataset.max || '100');
        const step = parseFloat(container.dataset.step || '1');
        const type = container.dataset.type || 'range';
        const isDiscrete = container.classList.contains('apf-range--discrete');
        let values = [];
        let labels = [];
        if (isDiscrete) {
            try {
                values = JSON.parse(container.dataset.values || '[]');
                labels = JSON.parse(container.dataset.labels || '[]');
            } catch (_) {}
        }
        const start = [min, max];
        const tooltips = true;
        const format = {
            to: function (value) {
                if (isDiscrete) {
                    const idx = Math.round(value);
                    return labels[idx] || values[idx] || idx;
                }
                if (type === 'price') {
                    return formatWooCommercePrice(value, false);
                }
                return Math.round(value);
            },
            from: function (value) {
                return Number(value);
            }
        };
        if (typeof noUiSlider !== 'undefined') {
            noUiSlider.create(sl, {
                start,
                connect: true,
                step: isDiscrete ? 1 : step,
                range: isDiscrete ? { min: 0, max: Math.max(0, values.length - 1) } : { min, max },
                tooltips,
                format
            });
            // After creating slider, check if there are URL values to set
            const taxonomy = container.dataset.taxonomy;
            const metaKey = container.dataset.meta_key;
            const urlParams = new URLSearchParams(window.location.search);
            
            // Check if this slider has values in URL
            let urlValue = null;
            if (type === 'price' && urlParams.has('price')) {
                urlValue = urlParams.get('price');
            } else if (taxonomy && urlParams.has(taxonomy)) {
                urlValue = urlParams.get(taxonomy);
            } else if (metaKey && urlParams.has(metaKey)) {
                urlValue = urlParams.get(metaKey);
            }
            
            // If URL has range values (contains comma), set them
            if (urlValue && urlValue.includes(',')) {
                const [minVal, maxVal] = urlValue.split(',');
                if (isDiscrete) {
                    const minIndex = values.indexOf(minVal);
                    const maxIndex = values.indexOf(maxVal);
                    if (minIndex !== -1 && maxIndex !== -1) {
                        sl.noUiSlider.set([minIndex, maxIndex]);
                        container.dataset.valuemin = minVal;
                        container.dataset.valuemax = maxVal;
                        console.log(`[noUiSlider] Set initial values from URL for ${taxonomy || metaKey || 'price'}: ${minVal}, ${maxVal}`);
                    }
                } else {
                    const minNum = parseFloat(minVal);
                    const maxNum = parseFloat(maxVal);
                    if (!isNaN(minNum) && !isNaN(maxNum)) {
                        sl.noUiSlider.set([minNum, maxNum]);
                        container.dataset.valuemin = String(Math.round(minNum));
                        container.dataset.valuemax = String(Math.round(maxNum));
                        console.log(`[noUiSlider] Set initial values from URL for ${taxonomy || metaKey || 'price'}: ${minNum}, ${maxNum}`);
                    }
                }
            }
            
            sl.noUiSlider.on('set', function(valuesArr, handle, unencoded) {
                // Store real values on container for FilterManager
                if (isDiscrete) {
                    const lowIdx = Math.round(unencoded[0]);
                    const highIdx = Math.round(unencoded[1]);
                    container.dataset.valuemin = values[lowIdx] ?? String(lowIdx);
                    container.dataset.valuemax = values[highIdx] ?? String(highIdx);
                } else {
                    container.dataset.valuemin = String(Math.round(unencoded[0]));
                    container.dataset.valuemax = String(Math.round(unencoded[1]));
                }

                console.log('[noUiSlider] Range slider changed', {
                    type: type,
                    valuemin: container.dataset.valuemin,
                    valuemax: container.dataset.valuemax,
                    container: container
                });

                // Call handleFilterChange with container to trigger filter update
                // Use setTimeout to ensure dataset values are updated before reading
                setTimeout(() => {
                    if (window.affiniteProductFilter && window.affiniteProductFilter.handleFilterChange) {
                        window.affiniteProductFilter.handleFilterChange(container);
                    }
                }, 0);
            });

            // Varianta s inputy - synchronizace slider <-> inputy
            const variant = container.dataset.variant;
            if (variant === 'inputs') {
                const inputMin = container.querySelector('.apf-range__input--min');
                const inputMax = container.querySelector('.apf-range__input--max');

                if (inputMin && inputMax) {
                    if (isDiscrete) {
                        // Pro diskrétní slidery - editovatelné inputy s labely
                        sl.noUiSlider.on('update', function(valuesArr, handle, unencoded) {
                            const minIdx = Math.round(unencoded[0]);
                            const maxIdx = Math.round(unencoded[1]);

                            // Aktualizovat labely v inputech
                            const minLabel = labels[minIdx] || values[minIdx] || minIdx;
                            const maxLabel = labels[maxIdx] || values[maxIdx] || maxIdx;

                            // Aktualizovat pouze pokud input není aktivní (uživatel nepíše)
                            if (document.activeElement !== inputMin && inputMin.value !== minLabel) {
                                inputMin.value = minLabel;
                                inputMin.dataset.index = minIdx;
                            }
                            if (document.activeElement !== inputMax && inputMax.value !== maxLabel) {
                                inputMax.value = maxLabel;
                                inputMax.dataset.index = maxIdx;
                            }
                        });

                        // Funkce pro nalezení nejbližšího indexu podle zadané hodnoty
                        const findClosestIndex = (inputValue) => {
                            // Extrahovat číselnou hodnotu z inputu
                            const inputNum = parseFloat(inputValue.replace(',', '.').replace(/[^\d.-]/g, ''));
                            if (isNaN(inputNum)) return -1;

                            let closestIdx = 0;
                            let closestDiff = Infinity;

                            for (let i = 0; i < labels.length; i++) {
                                const labelNum = parseFloat(labels[i].replace(',', '.').replace(/[^\d.-]/g, ''));
                                if (!isNaN(labelNum)) {
                                    const diff = Math.abs(labelNum - inputNum);
                                    if (diff < closestDiff) {
                                        closestDiff = diff;
                                        closestIdx = i;
                                    }
                                }
                            }
                            return closestIdx;
                        };

                        // Při změně inputu aktualizovat slider
                        // Spouští se pouze při blur (kliknutí mimo) nebo Enter
                        const handleDiscreteInputChange = function(isMinInput) {
                            let minIdx = parseInt(inputMin.dataset.index, 10) || 0;
                            let maxIdx = parseInt(inputMax.dataset.index, 10) || (labels.length - 1);

                            if (isMinInput) {
                                const newMinIdx = findClosestIndex(inputMin.value);
                                if (newMinIdx !== -1) minIdx = newMinIdx;
                            } else {
                                const newMaxIdx = findClosestIndex(inputMax.value);
                                if (newMaxIdx !== -1) maxIdx = newMaxIdx;
                            }

                            // Zajistit, že min <= max
                            if (minIdx > maxIdx) {
                                const temp = minIdx;
                                minIdx = maxIdx;
                                maxIdx = temp;
                            }

                            // Nastavit slider
                            sl.noUiSlider.set([minIdx, maxIdx]);
                        };

                        // Handler pro Enter klávesu
                        const handleDiscreteKeydown = function(e, isMinInput) {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleDiscreteInputChange(isMinInput);
                                e.target.blur(); // Odstranit focus z inputu
                            }
                        };

                        // Pouze change event (spustí se při blur nebo Enter)
                        inputMin.addEventListener('change', () => handleDiscreteInputChange(true));
                        inputMax.addEventListener('change', () => handleDiscreteInputChange(false));

                        // Keydown pro Enter
                        inputMin.addEventListener('keydown', (e) => handleDiscreteKeydown(e, true));
                        inputMax.addEventListener('keydown', (e) => handleDiscreteKeydown(e, false));

                        console.log('[noUiSlider] Discrete input synchronization initialized for', container.dataset.id);
                    } else {
                        // Pro kontinuální slidery (price) - editovatelné inputy
                        sl.noUiSlider.on('update', function(valuesArr, handle, unencoded) {
                            const minVal = Math.round(unencoded[0]);
                            const maxVal = Math.round(unencoded[1]);

                            // Aktualizovat pouze pokud se hodnota liší (prevence loop)
                            if (parseInt(inputMin.value, 10) !== minVal) {
                                inputMin.value = minVal;
                            }
                            if (parseInt(inputMax.value, 10) !== maxVal) {
                                inputMax.value = maxVal;
                            }
                        });

                        // Při změně inputu aktualizovat slider
                        // Spouští se pouze při blur (kliknutí mimo) nebo Enter
                        const handleInputChange = function() {
                            let minVal = parseInt(inputMin.value, 10) || min;
                            let maxVal = parseInt(inputMax.value, 10) || max;

                            // Omezit na rozsah
                            minVal = Math.max(min, Math.min(minVal, max));
                            maxVal = Math.max(min, Math.min(maxVal, max));

                            // Zajistit, že min <= max
                            if (minVal > maxVal) {
                                const temp = minVal;
                                minVal = maxVal;
                                maxVal = temp;
                            }

                            // Aktualizovat inputy s opravenými hodnotami
                            inputMin.value = minVal;
                            inputMax.value = maxVal;

                            // Nastavit slider
                            sl.noUiSlider.set([minVal, maxVal]);
                        };

                        // Handler pro Enter klávesu
                        const handleKeydown = function(e) {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleInputChange();
                                e.target.blur(); // Odstranit focus z inputu
                            }
                        };

                        // Pouze change event (spustí se při blur nebo Enter)
                        inputMin.addEventListener('change', handleInputChange);
                        inputMax.addEventListener('change', handleInputChange);

                        // Keydown pro Enter
                        inputMin.addEventListener('keydown', handleKeydown);
                        inputMax.addEventListener('keydown', handleKeydown);

                        console.log('[noUiSlider] Input synchronization initialized for', container.dataset.id);
                    }
                }
            }
        }
    });
});

